import React, { useRef, useEffect } from 'react';
import { Box, Link, useTheme } from '@mui/material';

interface SkipLink {
  label: string;
  href: string;
  target: string;
}

interface SkipNavigationProps {
  links?: SkipLink[];
}

const defaultLinks: SkipLink[] = [
  { label: 'Skip to main content', href: '#main-content', target: 'main' },
  { label: 'Skip to navigation', href: '#navigation', target: 'nav' },
  { label: 'Skip to search', href: '#search', target: '[role="search"]' },
];

export const SkipNavigation: React.FC<SkipNavigationProps> = ({ links = defaultLinks }) => {
  const theme = useTheme();
  const skipLinksRef = useRef<HTMLDivElement>(null);

  // Handle skip link clicks
  const handleSkipClick = (e: React.MouseEvent<HTMLAnchorElement>, target: string) => {
    e.preventDefault();

    let targetElement: HTMLElement | null = null;

    // Try different methods to find the target
    if (target.startsWith('#')) {
      // ID selector
      targetElement = document.querySelector(target);
    } else if (target.startsWith('[')) {
      // Attribute selector
      targetElement = document.querySelector(target);
    } else if (target === 'main' || target === 'nav') {
      // Element selector
      targetElement = document.querySelector(target);
    }

    if (targetElement) {
      // Make the target focusable if it isn't already
      if (!targetElement.hasAttribute('tabindex')) {
        targetElement.setAttribute('tabindex', '-1');
      }

      // Focus the target element
      targetElement.focus();

      // Smooth scroll to the target
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Ensure skip links are the first focusable elements
  useEffect(() => {
    const handleFirstTab = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        const skipLinksContainer = skipLinksRef.current;
        if (skipLinksContainer) {
          const activeElement = document.activeElement;
          const isSkipLinkFocused = skipLinksContainer.contains(activeElement);

          // If focus is not within skip links and this is the first tab
          if (!isSkipLinkFocused && activeElement === document.body) {
            e.preventDefault();
            const firstLink = skipLinksContainer.querySelector('a');
            if (firstLink) {
              (firstLink as HTMLElement).focus();
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleFirstTab);
    return () => document.removeEventListener('keydown', handleFirstTab);
  }, []);

  return (
    <Box
      ref={skipLinksRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: theme.zIndex.modal + 100, // Ensure it's above everything
        '& a': {
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          '&:focus': {
            position: 'fixed',
            top: theme.spacing(1),
            left: theme.spacing(1),
            width: 'auto',
            height: 'auto',
            padding: theme.spacing(2, 3),
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            textDecoration: 'none',
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[4],
            fontSize: '1rem',
            fontWeight: 500,
            zIndex: theme.zIndex.modal + 100,
            outline: `3px solid ${theme.palette.mode === 'dark' ? theme.palette.warning.main : theme.palette.warning.dark}`,
            outlineOffset: '2px',
          },
        },
      }}
    >
      {links.map((link, index) => (
        <Link
          key={link.href}
          href={link.href}
          onClick={(e) => handleSkipClick(e, link.target)}
          tabIndex={0}
          sx={{
            '&:not(:first-of-type):focus': {
              top: `${theme.spacing(1 + (index * 6))} !important`,
            },
          }}
        >
          {link.label}
        </Link>
      ))}
    </Box>
  );
};
